import React, { useState, useRef, useCallback } from "react";
import ModalAlert from "../modal/modal_alert";
import ModalAlertWithHtml from "../modal/modal_alert_with_html";
import axios from "../../sagas/axios";

const ReviewCreate = ({ event }) => {
  const [description, setDescription] = useState("");
  const [score, setScore] = useState(0)
  const modalAlertSuccessRef = useRef(null);
  const modalAlertShareReviewRef = useRef(null);
  const modalAlertErrorRef = useRef(null);
  const isProcessing = useRef(false);

  const onSubmit = useCallback(
    () => {
      if (isProcessing.current) return;
      isProcessing.current = true;

      const body = JSON.stringify({
        circle_id: event.circle.id,
        event_id: event.id,
        score: score,
        description: description,
      });
      axios
        .post("/api/circles_reviews", body)
        .then(() => {
          modalAlertSuccessRef.current.openModal();
        })
        .catch(() => {
          modalAlertErrorRef.current.openModal();
        });
    },
    [score, description, modalAlertSuccessRef, modalAlertErrorRef]
  );

  const toReportPage = () => {
    if (event.company_id) {
      location.href = "/community/" + event.company.id_to_param + "/circle/" + event.circle.id + "/events_for_redirect/" + event.id;

    } else {
      location.href = "/circle/" + event.circle.id + "/events_for_redirect/" + event.id;
    }
  };

  // 星の下に出す言葉（見た目のみ。送る値は今まで通り 1〜5 の数字）
  const scoreLabels = ["", "いまひとつ", "もう少し", "ふつう", "よかった", "とてもよかった"];

  // 星は押せることが分かるようボタンにする（キーボードでも選べる）。押したときの処理は今まで通り setScore
  const Stars = () => {

    return (
      <div className="rc-stars" role="radiogroup" aria-label="評価">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={score === n}
            aria-label={`${n}（${scoreLabels[n]}）`}
            className={"rc-star" + (score >= n ? " is-on" : "")}
            onClick={() => setScore(n)}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  return (
    event ? (
      <div className="rc-container">
        <h1 className="rc-title">レビューを書く</h1>

        {/* 参加したイベント。書く人が当日を思い出しやすいよう、イベントを中心に
            （左に大きく画像・右にタイトルと日付）。主催サークルはその中に1行で置き、押すとサークルページへ
            （デザイナー判断 2026-10-10。いったんサークルを上に大きく置く形を試したが、2段が羅列に見えたため） */}
        <div className="rc-event">
          <div className="rc-event__main">
            <img
              data-src={event.main_image_url_thumb}
              alt=""
              className="rc-event__img lazyloaded"
              onError={(e) => {
                e.target.src = "/images/ja2/img_noimage.gif";
              }}
              src={event.main_image_url_thumb}
            />
            <div className="rc-event__body">
              <p className="rc-event__label">参加したイベント</p>
              <p className="rc-event__title">{event.title}</p>
              {/* 日時と場所。改修ずみのイベントカード（event/_event_box_2026 の .ev26__meta）と同じ形:
                  日付は今のところ YYYY/MM/DD（赤。デザイナー指示 2026-10-10）｜ピン＋都道府県（北海道以外は「都・府・県」を外す）／オンライン */}
              <p className="rc-event__meta">
                <span className="rc-event__date">{event.event_date_disp}</span>
                {event.is_online ? (
                  <span className="rc-event__place icon icon-online">オンライン</span>
                ) : event.countries_pref ? (
                  <span className="rc-event__place icon icon-area-gray">
                    {event.countries_pref.name === "北海道" ? event.countries_pref.name : event.countries_pref.name.slice(0, -1)}
                  </span>
                ) : (
                  ""
                )}
              </p>
            </div>
          </div>
          {/* 主催サークル。イベントの下の帯に置き、帯全体を押すとサークルページへ。右に「›」だけ（デザイナー指示 2026-10-10） */}
          <a
            className="rc-host"
            href={event.company_id
              ? "/community/" + event.company.id_to_param + "/circle/" + event.circle.id
              : "/circle/" + event.circle.id}
          >
            <img
              data-src={event.circle.main_image_thumb}
              alt=""
              className="rc-host__icon lazyloaded"
              onError={(e) => {
                e.target.src = "/images/ja2/img_noimage.gif";
              }}
              src={event.circle.main_image_thumb}
            />
            <span className="rc-host__body">
              <span className="rc-host__label">主催サークル</span>
              <span className="rc-host__name">{event.circle.name}</span>
            </span>
            <span className="rc-host__more" aria-label="サークルを見る"><svg className="rc-host__chev" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg></span>
          </a>
        </div>

        {/* 入力欄。項目ごとに細い線で区切った1枚のカード（プロフィール登録と同じ作り） */}
        <div className="rc-card">
          <div className="rc-field">
            <p className="rc-label">
              評価<span className="rc-tag rc-tag--required">必須</span>
            </p>
            <Stars />
            <p className={"rc-stars__note" + (score > 0 ? " is-on" : "")}>
              {score > 0 ? scoreLabels[score] : "星をタップして選んでください"}
            </p>
          </div>
          <div className="rc-field">
            <label className="rc-label" htmlFor="description">
              メッセージ<span className="rc-tag">任意</span>
            </label>
            <div className="rc-textwrap">
              <textarea
                value={description}
                id="description"
                cols="5"
                rows="6"
                className="rc-textarea"
                placeholder={"例）初参加でしたが、みなさんが親切で楽しかったです。また参加したいです！"}
                onChange={(e) => setDescription(e.target.value)}
              />
              {/* 文字数の目安（表示のみ。1,000文字を超えると赤くなるが、入力・送信は止めない。入力欄の中の右下に置く（デザイナー指示 2026-10-10）。
                  本当に上限を設けるにはサーバー側の決まりが必要なのでエンジニアに依頼） */}
              <p className={"rc-count" + (description.length > 1000 ? " is-over" : "")}>
                {description.length.toLocaleString()} / 1,000
              </p>
            </div>
            {/* 注意点。入力欄の下に、うすいグレーの箱でまとめる（デザイナー案 2026-10-10） */}
            <div className="rc-notice">
              <p className="rc-notice__title">レビュー投稿について</p>
              <ul className="rc-notice__list">
                {/* 良い感想だけを促さないよう、公平な言い方にする（2026-10-10） */}
                <li>イベントに参加した感想を、これから参加する人の参考になるようにお聞かせください。</li>
                <li>同じイベントに何度か送ると、最新の1件だけが表示されます。</li>
                <li>個人が特定できる情報（LINE ID・電話番号など）は投稿しないでください。</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="rc-submitwrap">
          <button
            className="rc-submit"
            onClick={() => onSubmit()}
            disabled={score === 0}
          >
            送信する
          </button>
          {score === 0 ? <p className="rc-submit__note">評価を選ぶと送信できます</p> : ""}
        </div>
        <ModalAlert
          ref={modalAlertSuccessRef}
          title="レビュー投稿完了"
          content="レビューを投稿しました。"
          approveFunc={() => {
            if (score >= 4) {
              modalAlertShareReviewRef.current.openModal();
            } else {
              toReportPage();
            }
          }}
        />
        <ModalAlertWithHtml
          ref={modalAlertShareReviewRef}
          title="レビューをシェアしましょう！"
          approveButtonName="スキップ"
          approveFunc={toReportPage}
          content={() => {
            const redirectPath = `circle/${event.circle.id}/events_for_redirect/${event.id}`;
            const absoluteUrl = window.location.origin + '/' + redirectPath;
            const sharedUrl = encodeURIComponent(absoluteUrl);

            // Xシェアリンク
            const xText = `イベントに参加してきたよ！サークルで趣味やスポーツを楽しもう!%0A%0A${event.event_date_formatted}〜%0A${event.title}%0A%0A`;
            const xHref = `https://twitter.com/share?url=${sharedUrl}&related=tunagate&hashtags=つなげーと&text=${xText}`;
            
            // LINEシェアリンク
            const lineText = `${event.title} - イベントに参加してきたよ！サークルで趣味やスポーツを楽しもう！ つなげーと`;
            const lineHref = `http://line.me/R/msg/text/?${lineText} ${sharedUrl}`;

            // Facebookシェアリンク
            const facebookHref = `http://www.facebook.com/sharer.php?u=${sharedUrl}`;

            return (
              <div>
                <div className="text-center px-2">
                  <p className="mb-1">レビューの投稿ありがとうございます！</p>
                  <p className="mb-1">あなたのレビューをSNSでシェアして、</p>
                  <p className="mb-4">他の参加者にも役立ててもらいましょう！</p>
                </div>
                <a 
                target="_blank" 
                href={xHref} 
                className="btn btn-sm twitter-share-btn-modal my-3 d-flex align-items-center justify-content-center" 
                style={{color: "#fff", fontSize: "12px", fontWeight: "bold"}}>
                <img src="/img/logo-x_white.png" height="20" alt="X Logo" style={{position: "relative", top: "-2px"}} />
                  &nbsp;Post
                </a>
                <a 
                target="_blank" 
                href={lineHref}
                className="btn btn-sm line-share-btn-modal my-3 d-flex align-items-center justify-content-center"
                style={{color: "#fff", fontSize: "12px", fontWeight: "bold"}}>
                <i className="far fa-comment" ></i>
                  &nbsp;LINE
                </a>
                <a 
                target="_blank" 
                href={facebookHref} 
                className="btn btn-sm facebook-share-btn-modal my-3 d-flex align-items-center justify-content-center" 
                style={{color: "#fff", fontSize: "12px", fontWeight: "bold"}}>
                <i className="fab fa-facebook"></i>
                  &nbsp;facebook
                </a>
            </div>
            );}}
        />
        <ModalAlert
          ref={modalAlertErrorRef}
          title="レビュー投稿失敗"
          content="レビュー投稿に失敗しました。時間をおいてもう一度お試しください。"
        />
      </div>
    ) : (
      <div className="rc-container">
        <div className="rc-empty">
          <p className="rc-empty__title">レビューできるイベントがありません</p>
          <button
            className="rc-back"
            onClick={toReportPage}
          >
            戻る
          </button>
        </div>
      </div>
    )
  );
};

export default ReviewCreate;
